import { z } from 'zod/v4';

/**
 * The EDU course the widget is bound to — mirrors `EduCourse`
 * (`common/types/edu-course.type.ts`). The host stores whatever the courses API
 * returned, and the shape has grown over time, so every field except `id` is
 * `nullish()`: persisted widgets predating a field must still parse.
 *
 * The widget's own default is `course: null` (no course picked yet).
 */
export const eduCourseSchemaLegacy = z.object({
    id: z.number(),
    schoolId: z.number().nullish(),
    domainId: z.number().nullish(),
    landingId: z.number().nullish(),
    pageId: z.number().nullish(),
    userId: z.number().nullish(),
    name: z.string().nullish(),
    status: z.enum(['inactive', 'inProgress']).nullish(),
    description: z.string().nullish(),
    passingScore: z.number().nullish(),
    isLinear: z.boolean().nullish(),
    wasStarted: z.boolean().nullish(),
    startDate: z.string().nullish(),
    endDate: z.string().nullish(),
    needRegeneration: z.boolean().nullish(),
    hasPaidTariffs: z.boolean().nullish(),
});

export type EduCourseLegacy = z.infer<typeof eduCourseSchemaLegacy>;

/**
 * Denormalised snapshot of the selected course tariff, written by
 * `CourseTariffSelectorComponent` so the published page can render price and
 * payment details without a second request.
 *
 * The host type declares these as `T | undefined`, and JSON drops `undefined`
 * keys entirely — but an absent key means the signal form's FieldTree never
 * materialises a child for it, so reads and writes throw. Modelled as
 * `nullish()` (tolerates both absent and `null` in persisted data) while
 * {@link createLoginEduCourseTariffDetailsLegacy} seeds explicit `null`s, so
 * `WebLegacyWidgetSignalFormService`'s default-backfill always produces the keys.
 */
export const courseTariffDetailsSchemaLegacy = z.object({
    tariffName: z.string().nullish(),
    isPaid: z.boolean().nullish(),
    eduPaymentId: z.number().nullish(),
    price: z.number().nullish(),
    paymentMethodId: z.string().nullish(),
    paymentType: z.number().nullish(),
    paymentName: z.string().nullish(),
    currency: z.string().nullish(),
});

export type CourseTariffDetailsLegacy = z.infer<typeof courseTariffDetailsSchemaLegacy>;

export const createLoginEduCourseTariffDetailsLegacy = (
    overrides: Partial<CourseTariffDetailsLegacy> = {}
): CourseTariffDetailsLegacy => ({
    tariffName: null,
    isPaid: null,
    eduPaymentId: null,
    price: null,
    paymentMethodId: null,
    paymentType: null,
    paymentName: null,
    currency: null,
    ...overrides,
});
